import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableModule } from 'primeng/table';
import { TagComponent } from '../../../../shared/components/tag/tag.component';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { CameraAuditLogEntry, CameraStatus, CameraStatusService } from '../../services/camera-status.service';
import { Subscription } from 'rxjs';
import { NoRecordsComponent } from '../../../../shared/components/no-records/no-records.component';
import moment from 'moment';
import { changeTime, getDateOfServiceInYYYYMMDD } from '../../../../shared/utilities/utility-functions';

@Component({
  selector: 'app-camera-audit-log',
  standalone: true,
  imports: [TableModule, TagComponent, ButtonComponent, NoRecordsComponent],
  templateUrl: './camera-audit-log.component.html',
  styleUrl: './camera-audit-log.component.scss',
})
export class CameraAuditLogComponent implements OnInit, OnDestroy {
  config = inject(DynamicDialogConfig);
  ref = inject(DynamicDialogRef);

  rideId: string = '';
  lastSyncDate: any = '';
  auditLogs: CameraAuditLogEntry[] = [];
  private cameraStatusSubscription?: Subscription;
  private timezoneOffsetInSeconds: number = 0;

  ngOnInit(): void {
    console.log(this.config.data);
    this.rideId = this.config?.data?.rideId ?? '';
    const partner_id = this.config?.data?.partner_id;
    const cameraStatusService: CameraStatusService = this.config?.data?.cameraStatusService;
    const ride = this.config?.data?.ride;
    this.timezoneOffsetInSeconds = ride?.timezoneOffsetInSeconds ?? 0;

    // Format lastSyncDate with timezone awareness
    const lastSyncDateTimestamp = this.config?.data?.lastSyncDate;
    if (lastSyncDateTimestamp) {
      const utcDateString = this.convertFirestoreTimestampToISOString(lastSyncDateTimestamp);
      this.lastSyncDate = changeTime(utcDateString, 'h:mm A', this.timezoneOffsetInSeconds);
    }

    const { startTime, endTime } = this.getRideTimeWindow(ride);

    if (partner_id && cameraStatusService) {
      this.subscribeToAuditLogs(partner_id, cameraStatusService, startTime, endTime);
    }
  }

  /**
   * Subscribes to audit logs updates and filters them based on ride time window
   * @param partner_id - The partner ID to watch
   * @param cameraStatusService - The camera status service instance
   * @param filterStartTime - The moment to start filtering logs from (first stop time)
   * @param filterEndTime - The moment to end filtering logs at (last stop time)
   */
  private subscribeToAuditLogs(partner_id: number, cameraStatusService: CameraStatusService, filterStartTime: moment.Moment, filterEndTime: moment.Moment): void {
    this.cameraStatusSubscription = cameraStatusService.watchStatus(partner_id).subscribe((statuses: CameraStatus) => {
      console.log(statuses);
      const allAuditLogs = statuses.audit_logs ?? [];

      // Filter audit logs to only show those that occurred during the ride time window
      this.auditLogs = allAuditLogs
        .filter((log: CameraAuditLogEntry) => {
          const logTime = this.convertFirestoreTimestampToMoment(log.last_sync_date);
          return logTime.isSameOrAfter(filterStartTime) && logTime.isSameOrBefore(filterEndTime);
        })
        .map((log: CameraAuditLogEntry) => {
          const utcDateString = this.convertFirestoreTimestampToISOString(log.last_sync_date);
          return {
            ...log,
            formattedTime: changeTime(utcDateString, 'h:mm A', this.timezoneOffsetInSeconds),
          };
        });

      console.log('Filtered Audit Logs:', this.auditLogs);
    });
  }

  /**
   * Determines the ride time window for filtering audit logs
   * @param ride - The ride data containing timeline and info
   * @returns Object with startTime (first stop) and endTime (last stop) as moment objects in UTC
   */
  private getRideTimeWindow(ride: any): { startTime: moment.Moment; endTime: moment.Moment } {
    if (ride?.rideTimeline?.length > 0 && ride?.rideInfo) {
      const dateOfService = getDateOfServiceInYYYYMMDD(ride.rideInfo.dateOfService || ride.rideInfo.strDateOfService);

      // Get first stop time (start of ride) - prefer actual over scheduled
      const firstStop = ride.rideTimeline[0];
      const firstStopTimeUtc = firstStop.actualArrivalTimeUtc && firstStop.actualArrivalTimeUtc !== '00:00:00' ? firstStop.actualArrivalTimeUtc : firstStop.scheduled_arrival_time_utc;
      const startTime = moment.utc(`${dateOfService} ${firstStopTimeUtc}`, 'YYYY-MM-DD HH:mm:ss');

      // Get last stop time (end of ride) - prefer actual over scheduled
      const lastStopIndex = ride.rideTimeline.length - 1;
      const lastStop = ride.rideTimeline[lastStopIndex];
      const lastStopTimeUtc = lastStop.actualArrivalTimeUtc && lastStop.actualArrivalTimeUtc !== '00:00:00' ? lastStop.actualArrivalTimeUtc : lastStop.scheduled_arrival_time_utc;
      const endTime = moment.utc(`${dateOfService} ${lastStopTimeUtc}`, 'YYYY-MM-DD HH:mm:ss');

      console.log('Ride time window - Start:', startTime.format(), 'End:', endTime.format());

      return { startTime, endTime };
    }

    // Fallback to today's start and end if no timeline data
    return {
      startTime: moment.utc().startOf('day'),
      endTime: moment.utc().endOf('day'),
    };
  }

  /**
   * Converts Firestore timestamp format to moment object in UTC
   * @param timestamp - Firestore timestamp with seconds and nanoseconds or Date object
   * @returns moment object in UTC
   */
  private convertFirestoreTimestampToMoment(timestamp: any): moment.Moment {
    if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp) {
      // Convert Firestore timestamp (seconds + nanoseconds) to milliseconds, use UTC
      return moment.utc(timestamp.seconds * 1000);
    } else if (timestamp instanceof Date) {
      // Get the timestamp value (milliseconds since epoch) and create UTC moment
      return moment.utc(timestamp.valueOf());
    }
    // Fallback to current time in UTC if format is unexpected
    return moment.utc();
  }

  /**
   * Converts Firestore timestamp to ISO string for use with changeTime utility
   * @param timestamp - Firestore timestamp with seconds and nanoseconds or Date object
   * @returns ISO string in UTC format
   */
  private convertFirestoreTimestampToISOString(timestamp: any): string {
    if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp) {
      return new Date(timestamp.seconds * 1000).toISOString();
    } else if (timestamp instanceof Date) {
      return timestamp.toISOString();
    }
    return new Date().toISOString();
  }

  ngOnDestroy(): void {
    this.cameraStatusSubscription?.unsubscribe();
  }
}
